/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useMediaQuery } from 'usehooks-ts'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '~/components/UI/shadcn/accordion'

import AudioSingle from '../components/Player'
// import AudioPlayer from '../AudioPlayer/AudioPlayer'

import { isDevelopment } from '~/utils/misc'
import { Pause, Play } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getEntriesByIds } from '~/lib/contensis/misc'
import Loader from '~/components/UI/General/Loader/Loader'

interface AudioPlaylistProps {
  content: {
    strongColour?: boolean
    image?: {
      altText: string
      asset: { sys: { uri: string } }
    }
    showSeriesName?: boolean
    audio: AudioItem[]
  }
  layoutColour?: string
}

type AudioItem = {
  audioFile: { sys: { uri: string } }
  heading?: string
  copy?: string
  series?: { entryTitle: string }
  coverArt?: { asset: { sys: { uri: string } } }
}

const AudioPlaylist = ({ content, layoutColour }: AudioPlaylistProps) => {
  const { strongColour, image, showSeriesName, audio } = content
  const safeAudio = useMemo(() => (Array.isArray(audio) ? audio : []), [audio])
  const audioIds = safeAudio.map((item: any) => item?.sys?.id).filter(Boolean)

  const { data, isLoading, error } = useQuery({
    queryKey: ['audio-playlist', audioIds],
    queryFn: async () => (audioIds.length ? getEntriesByIds(audioIds) : []),
    enabled: audioIds.length > 0
  })

  const dataById = useMemo(
    () =>
      Array.isArray(data)
        ? data.reduce((acc: Record<string, any>, entry: any) => {
            acc[entry?.sys?.id] = entry
            return acc
          }, {})
        : {},
    [data]
  )

  const hydratedAudio = useMemo(
    () =>
      Array.isArray(safeAudio)
        ? safeAudio.map((item: any) =>
            item?.sys?.id ? dataById[item.sys.id] || item : item
          )
        : [],
    [safeAudio, dataById]
  )

  const foregroundColour = ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
    ? strongColour
      ? 'chartreuse'
      : 'white'
    : strongColour
      ? 'mid-blue'
      : 'ink-blue'

  const isDesktop = useMediaQuery('(min-width: 768px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  const [audioConfig, setAudioConfig] = useState({
    audioFile: '',
    artwork: '',
    showArtwork: false,
    series: ''
    // colour: ''
  })

  const [playing, setPlaying] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  // Helper function to update audio state
  const updateAudioState = useCallback(
    (index: number) => {
      const item = hydratedAudio[index]
      setAudioConfig({
        audioFile: item?.audioFile?.sys.uri || '',
        artwork: item?.coverArt?.asset.sys.uri || '',
        showArtwork: isDesktop ? true : false,
        series: item.series?.entryTitle || ''
        // colour: item.colour || ''
      })
      setActiveIndex(index)
    },
    [hydratedAudio, isDesktop]
  )

  useEffect(() => {
    if (hydratedAudio.length > 0) {
      updateAudioState(0)
    }
  }, [hydratedAudio, isDesktop, updateAudioState])

  // Handle track finish
  const handleTrackFinish = useCallback(() => {
    const nextIndex = (activeIndex + 1) % hydratedAudio.length
    updateAudioState(nextIndex)
    setPlaying(nextIndex !== 0) // Stop playing if it's the first track again
  }, [activeIndex, hydratedAudio.length, updateAudioState])

  // Handle play/pause toggle
  const handlePlayPause = useCallback(
    (isPlaying: boolean) => setPlaying(isPlaying),
    []
  )

  // Render play/pause icon
  const renderPlayPauseIcon = useCallback(
    (index: number) =>
      playing && activeIndex === index ? <Pause /> : <Play />,
    [playing, activeIndex]
  )

  // Memoised audio items for rendering
  const renderedAudioItems = useMemo(
    () =>
      hydratedAudio.map((item, index) => (
        <AccordionItem
          key={index}
          value={`item-${index}`}
          className='sm:pt-s px-s pl-0'
        >
          <div className='gap-xs flex *:grow'>
            <AccordionTrigger
              className='text-h3-sm sm:text-h3-md pb-xs-plus lg:text-h3-lg gap-s w-full grow cursor-pointer justify-start font-bold'
              hideChevron
              onClick={() => {
                updateAudioState(index)
                setPlaying(playing && activeIndex === index ? !playing : true)
              }}
            >
              {renderPlayPauseIcon(index)}
              {item.heading}
            </AccordionTrigger>
          </div>
          <AccordionContent className='pb-m text-base sm:pl-[40px]'>
            <p>{item.copy}</p>
          </AccordionContent>
        </AccordionItem>
      )),
    [hydratedAudio, activeIndex, playing, updateAudioState, renderPlayPauseIcon]
  )

  if (isLoading)
    return (
      <div className='py-l flex w-full justify-center'>
        <Loader fill={'#f5b6dc'} />
      </div>
    )
  if (error) return <div>Error loading audio.</div>

  return (
    <div className='gap-s flex flex-col'>
      <AudioSingle
        isStandalone={false}
        tag={showSeriesName ? audioConfig.series : ''}
        music={audioConfig.audioFile}
        colour={foregroundColour}
        artwork={
          (isDevelopment && !audioConfig.artwork?.startsWith('https')
            ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
            : '') + (!image ? audioConfig.artwork : image?.asset?.sys.uri)
        }
        showArtwork={isDesktop ? true : false}
        align={undefined}
        playing={playing}
        onFinish={handleTrackFinish} // Callback function that is called when the track finishes playing
        onPlayPause={handlePlayPause} // Callback function that is called when the play/pause button is clicked
      >
        <Accordion
          type='single'
          className='gap-m flex w-full flex-col'
          value={`item-${activeIndex}`}
        >
          {renderedAudioItems}
        </Accordion>
      </AudioSingle>
    </div>
  )
}

export default AudioPlaylist
